import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';
import { Box, IconButton, Popover } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { flushSync } from 'react-dom';
import { SecondaryNavigationInfoLabel } from 'components/ui';
import { gridShortDate, isValidDate } from 'utilities';
import { FinancialsDateSelectorProps } from './FinancialsDateSelector.props';

const HiddenField = () => <div />;

const FinancialsDateSelectorPopover = ({ onDateChange, currentDate, disabled = false }: FinancialsDateSelectorProps) => {
  const [inputValue, setInputValue] = useState(() => (currentDate ? gridShortDate(currentDate) : ''));
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const chipRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setInputValue(currentDate ? gridShortDate(currentDate) : '');
  }, [currentDate]);

  useEffect(() => () => {
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
    } 
  }, []);

  const flashSuccess = useCallback(() => {
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
    }
    setShowSuccess(true);
    successTimerRef.current = setTimeout(() => setShowSuccess(false), 1200);
  }, []);

  const handleClose = useCallback(() => {
    setPopoverOpen(false);
    setPickerOpen(false);
    setHasError(false);
  }, []);

  const commitDate = useCallback(
    (text: string) => {
      if (!text.trim()) {
        setHasError(false);
        return;
      }
      if (isValidDate(text)) {
        const formatted = gridShortDate(text);
        flushSync(() => {
          setInputValue(formatted);
          setHasError(false);
          flashSuccess();
        });
        onDateChange(formatted);
        handleClose();
      } else {
        setHasError(true);
      }
    },
    [onDateChange, flashSuccess, handleClose]
  );

  const handleOpen = useCallback(() => {
    if (disabled) {
      return;
    }
    // Sync input to the latest committed date when opening.
    setInputValue(currentDate ? gridShortDate(currentDate) : '');
    setHasError(false);
    setPopoverOpen(true);
  }, [disabled, currentDate]);

  const handleChipKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpen();
    }
  }, [handleOpen]);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (hasError) {
      setHasError(false);
    }
  }, [hasError]);

  const handleInputKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitDate(inputValue);
    }
    if (e.key === 'Escape') {
      handleClose();
    }
  }, [commitDate, handleClose, inputValue]);

  const handleBlur = useCallback(() => commitDate(inputValue), [commitDate, inputValue]);

  const handlePickerAccept = useCallback(
    (date: Date | null) => {
      if (date) {
        const formatted = gridShortDate(date);
        flushSync(() => {
          setInputValue(formatted);
          setHasError(false);
          flashSuccess();
        });
        onDateChange(formatted);
        handleClose();
      }
      setPickerOpen(false);
    },
    [onDateChange, flashSuccess, handleClose]
  );

  const chipValue = currentDate ? gridShortDate(currentDate) : '—';
  const dateValue = inputValue ? moment(inputValue, 'MM/DD/YYYY').toDate() : null;

  return (
    <>
      <Box
        ref={chipRef}
        component="span"
        onClick={handleOpen}
        onKeyDown={handleChipKeyDown}
        role="button"
        tabIndex={disabled ? -1 : 0}
        style={{
          display: 'inline-flex',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        }}>
        <SecondaryNavigationInfoLabel
          label="Financials Date"
          valueTone={showSuccess ? 'positive' : 'brand'}
          value={chipValue}
        />
      </Box>

      <Popover
        open={popoverOpen}
        anchorEl={chipRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5 } } }}>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5, p: 1 }}>
          <Box
            ref={inputRef}
            component="input"
            autoFocus
            onBlur={handleBlur}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            placeholder="MM/DD/YYYY"
            type="text"
            value={inputValue}
            sx={{
              border: '1px solid',
              borderColor: hasError ? 'error.main' : 'divider',
              borderRadius: '4px',
              color: hasError ? 'error.main' : 'text.primary',
              fontSize: '0.8125rem',
              outline: 'none',
              px: 1,
              py: 0.5,
              width: '100px',
              '&:focus': { borderColor: hasError ? 'error.main' : 'primary.main' },
            }}
          />
          <IconButton
            size="small"
            onClick={() => setPickerOpen(true)}
            sx={{ color: 'action.active' }}>
            <CalendarIcon fontSize="small" />
          </IconButton>
        </Box>
      </Popover>

      <DatePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        value={dateValue}
        onAccept={handlePickerAccept}
        openTo="day"
        views={['year', 'month', 'day']}
        slots={{ field: HiddenField }}
        slotProps={{ popper: { anchorEl: chipRef.current } }}
      />
    </>
  );
};

export default FinancialsDateSelectorPopover;
